'use client';

import { formatPace } from '@streetferret/utils';
import { usePreferences } from '@streetferret/providers';
import { convertDistance } from '@turf/turf';
import { type Unit } from '@streetferret/providers';

interface PaceProps {
  paceInSeconds: number;
  /** Unit the pace value is in. When omitted, assumes it matches user preference (e.g. from listActivities) */
  originalUnit?: Unit;
}

export function Pace({ paceInSeconds, originalUnit }: PaceProps) {
  const { unit } = usePreferences();
  const unitNiceName = unit === 'imperial' ? 'mi' : 'km';

  // Only convert when originalUnit is provided and differs from display unit.
  // listActivities/feed returns pace in user's unit - omit originalUnit, no conversion.
  // getWithCityDetails returns runs in sec/mile, rides in sec/km - pass originalUnit.
  const inputIsMiles = originalUnit === 'imperial';
  const displayIsMiles = unit === 'imperial';
  const displayIsKm = !displayIsMiles;
  const needsConversion =
    originalUnit !== undefined && inputIsMiles !== displayIsMiles;

  const milesPerKm = convertDistance(1, 'miles', 'kilometers');
  const convertedPace =
    needsConversion && inputIsMiles && displayIsKm
      ? paceInSeconds / milesPerKm // sec/mi → sec/km
      : needsConversion && !inputIsMiles && displayIsMiles
        ? paceInSeconds * milesPerKm // sec/km → sec/mi
        : paceInSeconds;

  const pace = formatPace(convertedPace);

  return (
    <div className="tabular-nums">
      <span>{pace}</span>
      <span>/</span>
      <span>{unitNiceName}</span>
    </div>
  );
}
